import React, { useState } from 'react';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formsubmit.co/ajax/robin@vortexiasolutions.com.au', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const label = "font-mono text-xs uppercase tracking-[0.2em] text-[#E8490F]";

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1C1C1C] font-serif antialiased">
      <div className="max-w-3xl mx-auto px-6">

        {/* Masthead */}
        <header className="pt-10 pb-6 flex items-baseline justify-between">
          <div className="flex items-center gap-3">
            <img src="/vortexialogo_no background_only the disc.png" alt="" className="h-6 w-6" />
            <span className="text-xl tracking-tight">Vortexia Solutions</span>
          </div>
          <nav className="font-mono text-xs uppercase tracking-[0.2em] space-x-8">
            <a href="#work" className="hover:text-[#E8490F]">Work</a>
            <a href="#contact" className="hover:text-[#E8490F]">Contact</a>
          </nav>
        </header>
        <hr className="border-t-2 border-[#1C1C1C]" />

        {/* Intro */}
        <section className="py-16">
          <p className={label + " mb-6"}>Software studio / Australia</p>
          <h1 className="text-4xl md:text-5xl leading-[1.15] tracking-tight">
            We build web applications for engineering and research.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#454545] max-w-2xl">
            Calculation tools that put published methods in the browser, data pipelines
            that behave, and language-model features that earn their place. A small
            studio doing direct work with universities and industry.
          </p>
        </section>

        <hr className="border-t border-[#1C1C1C]/25" />

        {/* What we do */}
        <section className="py-14">
          <p className={label + " mb-8"}>01 / What we do</p>
          <dl>
            <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] gap-1 sm:gap-6 py-4 border-b border-[#1C1C1C]/15">
              <dt className="font-medium">Engineering web apps</dt>
              <dd className="text-[#454545]">Peer-reviewed methods and calculators, delivered as fast
              web tools that engineers actually use.</dd>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] gap-1 sm:gap-6 py-4 border-b border-[#1C1C1C]/15">
              <dt className="font-medium">Data management</dt>
              <dd className="text-[#454545]">Pipelines, dashboards and databases for research and
              operations data.</dd>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] gap-1 sm:gap-6 py-4 border-b border-[#1C1C1C]/15">
              <dt className="font-medium">LLM integration</dt>
              <dd className="text-[#454545]">Language models built into real workflows, not bolted on.</dd>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] gap-1 sm:gap-6 py-4">
              <dt className="font-medium">One-off projects</dt>
              <dd className="text-[#454545]">Integrations and odd jobs that do not fit a category.</dd>
            </div>
          </dl>
        </section>

        <hr className="border-t border-[#1C1C1C]/25" />

        {/* Selected work */}
        <section id="work" className="py-14 scroll-mt-8">
          <p className={label + " mb-8"}>02 / Selected work</p>
          <h2 className="text-2xl md:text-3xl tracking-tight mb-4">UWA CPT Pile Calculator</h2>
          <p className="text-[#454545] leading-relaxed max-w-2xl mb-4">
            Built with Professor Barry Lehane's group at The University of Western Australia.
            The calculator turns raw cone penetration test data into pile and foundation
            designs: six modules implementing peer-reviewed UWA methods for driven, bored
            and helical (screw) piles, shallow footings, monopiles and embedded retaining
            walls. It reads real contractor CPT files, and it is free with no sign-up.
          </p>
          <figure className="my-8">
            <a href="https://uwa-geotech-cpt-calculator.com" target="_blank" rel="noreferrer">
              <img
                src="/uwa-cpt-calculator.png"
                alt="UWA CPT Pile Calculator web application showing the six calculation modules"
                className="w-full border border-[#1C1C1C]/30"
              />
            </a>
            <figcaption className="font-mono text-xs text-[#666] mt-3">
              Fig. 1 &nbsp; The six-module calculator, live at uwa-geotech-cpt-calculator.com
            </figcaption>
          </figure>
          <dl className="font-mono text-sm text-[#454545] space-y-1 mb-8">
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-[#999]">Methods</dt>
              <dd>Lehane et al. (2020), Doan &amp; Lehane (2021), Bittar et al. (2023)</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-[#999]">Input</dt>
              <dd>CSV, Excel, AGS4 and raw contractor CPT files</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-[#999]">Access</dt>
              <dd>Free, no account, used by engineers worldwide</dd>
            </div>
          </dl>
          <a
            href="https://uwa-geotech-cpt-calculator.com"
            target="_blank"
            rel="noreferrer"
            className="inline-block font-mono text-sm uppercase tracking-[0.15em] border border-[#1C1C1C] px-6 py-3 hover:bg-[#1C1C1C] hover:text-[#FAF7F2] transition-colors"
          >
            Open the calculator ↗
          </a>
        </section>

        <hr className="border-t border-[#1C1C1C]/25" />

        {/* Contact */}
        <section id="contact" className="py-14 scroll-mt-8">
          <p className={label + " mb-8"}>03 / Contact</p>
          <p className="text-[#454545] leading-relaxed max-w-2xl mb-10">
            Have a research method, calculation or dataset that belongs on the web?
            Write to <a href="mailto:robin@vortexiasolutions.com.au" className="text-[#1C1C1C] underline decoration-[#E8490F] decoration-2 underline-offset-4 hover:text-[#E8490F]">robin@vortexiasolutions.com.au</a> or
            use the form.
          </p>
          <form onSubmit={handleSubmit} className="max-w-xl space-y-8">
            <div>
              <label htmlFor="name" className="block font-mono text-xs uppercase tracking-[0.2em] text-[#666] mb-2">Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-0 border-b border-[#1C1C1C]/40 focus:border-[#E8490F] focus:ring-0 px-0 py-2 font-serif text-base"
              />
            </div>
            <div>
              <label htmlFor="email" className="block font-mono text-xs uppercase tracking-[0.2em] text-[#666] mb-2">Email</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-0 border-b border-[#1C1C1C]/40 focus:border-[#E8490F] focus:ring-0 px-0 py-2 font-serif text-base"
              />
            </div>
            <div>
              <label htmlFor="message" className="block font-mono text-xs uppercase tracking-[0.2em] text-[#666] mb-2">Message</label>
              <textarea
                id="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-0 border-b border-[#1C1C1C]/40 focus:border-[#E8490F] focus:ring-0 px-0 py-2 font-serif text-base resize-y"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`font-mono text-sm uppercase tracking-[0.15em] border border-[#1C1C1C] px-6 py-3 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#1C1C1C] hover:text-[#FAF7F2]'}`}
            >
              {isSubmitting ? 'Sending...' : 'Send message'}
            </button>
            {submitStatus === 'success' && (
              <p className="font-mono text-sm text-[#1C1C1C]">Message sent. We will get back to you soon.</p>
            )}
            {submitStatus === 'error' && (
              <p className="font-mono text-sm text-[#B3261E]">Something went wrong. Please email us instead.</p>
            )}
          </form>
        </section>

        {/* Footer */}
        <hr className="border-t-2 border-[#1C1C1C]" />
        <footer className="py-8 flex items-baseline justify-between font-mono text-xs text-[#666]">
          <span>© {new Date().getFullYear()} Vortexia Solutions</span>
          <span>Australia</span>
        </footer>
      </div>
    </div>
  );
}

export default App;
